import React from "react";
import { EditorBlock } from "draft-js";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Parent } from "pym.js";
import EmbedPopover from "editor/components/EmbedPopover";
import Placeholder from "editor/components/Placeholder";
import {
  EMBED_CAPTION_PLACEHOLDER,
  IMAGE_CAPTION_PLACEHOLDER,
  EMBED_DEPENDENCY,
  FACEBOOK_SDK_VERSION,
} from "config/constants/editor";

import styles from "./Embed.css";

export default class Embed extends React.Component {
  constructor() {
    super();
    this.state = {
      embedEl: null,
      isActive: false,
    };
    this.addEmbedDependencies = this._addEmbedDependencies.bind(this);
    this.activate = this._activate.bind(this);
    this.deactivate = this._deactivate.bind(this);
    this.renderEmbed = this._renderEmbed.bind(this);
  }

  _activate() {
    this.setState({ isActive: true });
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur(); // Remove focus from the editor.
    }
  }

  _deactivate() {
    this.setState({ isActive: false });
  }

  _addEmbedDependencies(jsScriptClass, callback, embedDependency) {
    const appendJsLib = (url, className, callback) => {
      let jsLib = document.createElement("script");
      jsLib.async = true;
      jsLib.charset = "utf-8";
      jsLib.type = "text/javascript";
      jsLib.src = url;
      jsLib.className = className;
      if (className === "fb-embed-lib") jsLib.id = "facebook-jssdk";
      jsLib.onload = callback;
      document.getElementsByTagName("head")[0].appendChild(jsLib);
    };

    const jsLibNode = document.getElementsByClassName(jsScriptClass);
    // JS lib hasn't already been inserted into document, so add it
    if (jsLibNode.length === 0) {
      appendJsLib(embedDependency, jsScriptClass, callback);
    } else {
      // JS lib has already been inserted into document, so use the existing lib
      jsLibNode[0].onload = callback();
    }
  }

  _renderEmbed(oembed, type) {
    let conditionalCaption = null;
    let conditionalPlaceholder = null;
    let embedHtml = null;

    // if it's an axios visual or a photo, add a caption
    if (
      type === "axios-visual" ||
      type === "datawrapper" ||
      oembed.type === "photo"
    ) {
      conditionalPlaceholder =
        oembed.type === "photo"
          ? IMAGE_CAPTION_PLACEHOLDER
          : EMBED_CAPTION_PLACEHOLDER;
      conditionalCaption = (
        <figcaption>
          <Placeholder
            className={styles.placeholder}
            isVisible={this.props.block.getText().length === 0}
          >
            {conditionalPlaceholder}
          </Placeholder>
          <EditorBlock {...this.props} />
        </figcaption>
      );
    }

    // otherwise if it's a photo embed (like a giphy)
    // use an image tag
    if (oembed.type === "photo") {
      embedHtml = (
        <figure>
          <img
            src={oembed.url}
            alt={oembed.title}
            onClick={this.activate}
            ref={el => {
              if (!this.state.embedEl) this.setState({ embedEl: el });
            }}
          />
          {conditionalCaption}
        </figure>
      );
      // or just embed the html as-in
    } else {
      embedHtml = (
        <figure>
          <div
            className={classnames(styles.embedContainer, {
              [styles.responsiveIframe]:
                type === "youtube" ||
                type === "vimeo" ||
                type === "jwplayer-video",
            })}
            onClick={this.activate}
            contentEditable={false}
            dangerouslySetInnerHTML={{ __html: oembed.html }}
            ref={el => {
              if (!this.state.embedEl) this.setState({ embedEl: el });
            }}
          />
          {conditionalCaption}
        </figure>
      );
    }
    return embedHtml;
  }

  componentDidMount() {
    const { oembed, type } = this.props.block.getData().toJSON();
    let jsScriptClass, callback;
    switch (type) {
      case "facebook-post":
      case "facebook-video":
        window.fbAsyncInit = () => {
          window.FB.init({
            xfbml: true,
            version: FACEBOOK_SDK_VERSION,
          });
        };
        jsScriptClass = "fb-embed-lib";
        callback = () => {
          if (window.FB) {
            window.FB.init({
              appId: 650172611997232,
              xfbml: true,
              version: FACEBOOK_SDK_VERSION,
            });
            window.FB.XFBML.parse();
          }
        };
        this.addEmbedDependencies(
          jsScriptClass,
          callback,
          EMBED_DEPENDENCY.FACEBOOK
        );
        break;
      case "instagram":
        jsScriptClass = "ig-embed-lib";
        callback = () => {
          let instgrm = window.instgrm;
          if (instgrm) instgrm.Embeds.process();
        };
        this.addEmbedDependencies(
          jsScriptClass,
          callback,
          EMBED_DEPENDENCY.INSTAGRAM
        );
        break;
      case "twitter":
        jsScriptClass = "tw-embed-lib";
        callback = () => {
          let twttr = window.twttr;
          if (twttr && twttr.widgets && twttr.widgets.load)
            twttr.widgets.load();
        };
        this.addEmbedDependencies(
          jsScriptClass,
          callback,
          EMBED_DEPENDENCY.TWITTER
        );
        break;
      case "axios-visual":
        jsScriptClass = "pym-embed-lib";
        new Parent(oembed.slug, oembed.url);
        break;
      default:
        break;
    }
  }

  render() {
    const embedDataJSON = this.props.block.getData().toJSON();
    const { oembed, type } = embedDataJSON;
    if (!oembed) return false;
    // handle old embeds that don't have config object
    const embedData = {
      config: {},
      ...embedDataJSON,
    };

    return (
      <div className={styles.root}>
        <EmbedPopover
          closeModal={this.deactivate}
          embedEl={this.state.embedEl}
          embedData={embedData}
          blockKey={this.props.block.getKey()}
          isActive={this.state.isActive}
        />
        {this.renderEmbed(oembed, type)}
      </div>
    );
  }
}

Embed.propTypes = {
  block: PropTypes.object.isRequired,
};
